import React from 'react'
import styled from 'styled-components'
import SectionHeader from '../SectionHeader'
import { colors, layout, bp } from '../../../theme'

const Main = styled.section`
  padding: ${layout.section};
  background: ${colors.mist};
  border-top: 1px solid ${colors.line};
  border-bottom: 1px solid ${colors.line};
`

const Container = styled.div`
  max-width: ${layout.maxWidth};
  margin: auto;
`

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;

  @media (max-width: ${bp.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${bp.mobile}) {
    grid-template-columns: 1fr;
  }
`

const Step = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.line};
  border-radius: ${layout.radius};
  padding: 1.3rem;

  .n {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: ${colors.green};
    color: ${colors.white};
    font-weight: 800;
    margin-bottom: 0.75rem;
  }

  h4 {
    margin: 0 0 0.4rem;
    color: ${colors.ink};
    font-size: 1rem;
  }

  p {
    margin: 0;
    color: ${colors.slate};
    font-size: 0.88rem;
    line-height: 1.5;
  }
`

const Split = styled.div`
  margin-top: 2.25rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: ${bp.tablet}) {
    grid-template-columns: 1fr;
  }
`

const Panel = styled.div`
  background: ${colors.white};
  border: 1px solid ${colors.line};
  border-radius: ${layout.radius};
  padding: 1.75rem;

  h3 {
    margin: 0 0 1rem;
    color: ${colors.ink};
    font-size: 1.2rem;
  }
`

const WhyList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    gap: 0.6rem;
    color: ${colors.slate};
    line-height: 1.55;
    margin-bottom: 0.7rem;
    font-size: 0.94rem;
  }

  li b {
    color: ${colors.ink};
  }

  .tick {
    color: ${colors.green};
    font-weight: 800;
    flex-shrink: 0;
  }
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.5rem;
`

const Chip = styled.span`
  background: ${colors.greenLight};
  color: ${colors.greenDark};
  border-radius: 999px;
  padding: 0.4rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 600;
`

const RoadNote = styled.p`
  color: ${colors.slate};
  font-size: 0.92rem;
  line-height: 1.6;
  margin: 0 0 1rem;
`

// Sourced from the company profile "How it works" and expansion roadmap (assets/).
const steps = [
  { t: 'Manufacture', d: 'Quality-controlled detergents loaded into 20L / 50L bulk refill containers.' },
  { t: 'Smart Dispense', d: 'Precision dosing — customers buy detergent from as low as KES 10 per use.' },
  { t: 'NBIoT Sensors', d: 'Auto-track soap levels and trigger refill alerts before the unit runs dry.' },
  { t: 'Cashless Pay', d: 'M-Pesa / Paybill-enabled — no cash needed at the point of use.' },
  { t: 'Real-time Data', d: 'Litres dispensed, usage patterns, and ESG impact reporting.' },
]

const why = [
  ['Works anywhere', 'reliable in low-signal, remote, and basement environments where WiFi fails.'],
  ['Ultra-low power', 'battery lasts months to years — no regular charging.'],
  ['Affordable at scale', 'low-cost, low-bandwidth connectivity built for national rollout.'],
  ['Purpose-built sensing', 'designed for soap levels, flow, and refill alerts, not generic IoT.'],
  ['Field-resilient', 'simple to deploy and maintain in harsh industrial conditions.'],
]

const roadmap = [
  'Handwash Gel', 'Disinfectant', 'Bleach', 'Shower Gel',
  'Degreaser', 'Dishwashing Liquid', 'Sanitizer', 'Tiles Cleaner',
]

const Dispenser = () => (
  <Main id="dispenser">
    <Container>
      <SectionHeader
        kicker="Flagship Innovation"
        title="The NBIoT EcoSmart Dispenser"
        sub="A smart ATM for detergent — dispensing bulk product by the litre with precision dosing, cashless M-Pesa payments, and narrowband-IoT sensors that keep it stocked. The first prototype is built, branded, and live in a retail environment."
      />

      <Steps>
        {steps.map((s, i) => (
          <Step key={s.t}>
            <div className="n">{i + 1}</div>
            <h4>{s.t}</h4>
            <p>{s.d}</p>
          </Step>
        ))}
      </Steps>

      <Split>
        <Panel>
          <h3>Why NBIoT — not standard IoT?</h3>
          <WhyList>
            {why.map(([b, rest]) => (
              <li key={b}>
                <span className="tick">✓</span>
                <span>
                  <b>{b}</b> — {rest}
                </span>
              </li>
            ))}
          </WhyList>
        </Panel>

        <Panel>
          <h3>One machine, many products</h3>
          <RoadNote>
            The current prototype dispenses Multipurpose Liquid Detergent. The same NBIoT
            platform is being expanded so a single installation can dispense multiple SKUs —
            a complete, one-stop hygiene solution from one unit.
          </RoadNote>
          <Chips>
            {roadmap.map((r) => (
              <Chip key={r}>{r}</Chip>
            ))}
          </Chips>
        </Panel>
      </Split>
    </Container>
  </Main>
)

export default Dispenser
